/**
 * Single decision point for clarification thread deep-linking, shared by both surfaces
 * that render clarifications from URL search params:
 *
 * - the right-sidebar `ClarificationRequestsPanel` (single pane) — requires `crv === "t"`;
 * - the project-level split view (`SpecificationClarificationRequestsPage`) — treats any
 *   valid selected id as a thread selection (it does not require `crv`).
 *
 * The two surfaces differ ONLY in their activation policy; everything else (loading,
 * scope settling, stale-selection cleanup) is identical and lives here.
 *
 * Key invariant (LAT-575 navigation fix): the deep-link URL intent must not be destroyed
 * until the app has authoritative, route-matched data proving the selection is stale. So
 * the resolver returns `pending` — never `list` with a clear — while the scoped data is
 * loading or the route's scope is not yet settled. Callers may only rewrite the URL when
 * `shouldClearStaleSelection` is true.
 */

export type ClarificationActivationPolicy =
  /** Right sidebar: a thread shows only when the URL view mode is explicitly "thread". */
  | "requiresThreadViewParam"
  /** Project split pane: a valid selected id is itself a thread selection. */
  | "selectedIdImpliesThread";

export type ClarificationDeepLinkResolution =
  | { mode: "thread"; clarificationId: string; shouldClearStaleSelection: false }
  | { mode: "list"; selectedId: string | null; shouldClearStaleSelection: boolean }
  | { mode: "pending"; selectedId: string | null; shouldClearStaleSelection: false };

export interface ClarificationDeepLinkInput {
  policy: ClarificationActivationPolicy;
  viewMode: "list" | "thread";
  selectedId: string | null;
  isCreating: boolean;
  /** Active clarification list for the current scope, or `undefined` while loading. */
  scopedData: ReadonlyArray<{ _id: string }> | undefined;
  /**
   * True once the active scope is authoritative for the current route — i.e. the
   * right-sidebar entity context matches the route (or the route is project-scoped).
   * A genuinely-absent selection may only be cleared when this is true.
   */
  contextSettled: boolean;
}

function isThreadActivationRequested(
  policy: ClarificationActivationPolicy,
  viewMode: "list" | "thread",
  selectedId: string | null
): selectedId is string {
  if (!selectedId) return false;
  return policy === "selectedIdImpliesThread" || viewMode === "thread";
}

export function resolveClarificationDeepLinkView(
  input: ClarificationDeepLinkInput
): ClarificationDeepLinkResolution {
  const { policy, viewMode, selectedId, isCreating, scopedData, contextSettled } = input;

  if (isCreating) {
    return { mode: "pending", selectedId, shouldClearStaleSelection: false };
  }

  if (!isThreadActivationRequested(policy, viewMode, selectedId)) {
    return { mode: "list", selectedId, shouldClearStaleSelection: false };
  }

  // Show the thread as soon as ANY authoritative list proves the id exists — including
  // the all-inclusive project list during the cold entity-context window. "Visible" is
  // trustworthy even before the scope fully settles.
  if (scopedData?.some((clarification) => clarification._id === selectedId)) {
    return { mode: "thread", clarificationId: selectedId, shouldClearStaleSelection: false };
  }

  // Not visible yet. We can only treat "absent" as authoritative once the data has
  // loaded AND the scope is settled for this route. Otherwise wait without touching the
  // URL, so a transient miss never collapses the thread.
  if (scopedData === undefined || !contextSettled) {
    return { mode: "pending", selectedId, shouldClearStaleSelection: false };
  }

  // Settled scope, resolved list, id genuinely absent: a stale selection. Safe to clear.
  return { mode: "list", selectedId, shouldClearStaleSelection: true };
}
